import pinyin from "pinyin";

// Common Chinese characters with their pinyin pronunciations
// This is a simplified dictionary for demonstration - in production, you'd want a more comprehensive one
const pinyinToHanziMap: Record<string, string[]> = {
  // Common single-character words
  xi: ["洗", "喜", "西", "系"],
  shou: ["手", "收", "受", "首"],
  jian: ["间", "见", "建", "简"],
  shang: ["商", "上", "伤", "赏"],
  dian: ["店", "点", "电", "典"],
  can: ["餐", "残", "参", "蚕"],
  guan: ["馆", "管", "观", "官"],

  // Common two-character combinations for better matching
  xishou: ["洗手"],
  xishoujian: ["洗手间"],
  shangdian: ["商店"],
  canguan: ["餐馆"],
  cesuo: ["厕所"],
  wc: ["厕所"],
  tushuguan: ["图书馆"],
  yinhang: ["银行"],
  yiyuan: ["医院"],
  xuexiao: ["学校"],
  jichang: ["机场"],
  huochezhan: ["火车站"],
  qichezhan: ["汽车站"],
  jiudian: ["酒店"],
  luguan: ["旅馆"],
  chuzuche: ["出租车"],
  gongjiaoche: ["公交车"],
  ditie: ["地铁"],
  dianti: ["电梯"],
  louti: ["楼梯"],
  tingliang: ["停留"],
  churukou: ["出入口"],
  anquan: ["安全"],
  jinjichukuo: ["紧急出口"],
  xinxi: ["信息"],
  bangzhu: ["帮助"],
  fuwu: ["服务"],
  zhongxin: ["中心"],
  da: ["大"],
  xiao: ["小"],
  qian: ["前"],
  hou: ["后"],
  zuo: ["左"],
  you: ["右"],
  xia: ["下"],
  li: ["里"],
  wai: ["外"],
  dong: ["东"],
  nan: ["南"],
  bei: ["北"],
};

export interface PinyinCandidate {
  text: string;
  pinyin: string;
  frequency: number;
}

export class PinyinService {
  /**
   * Convert Pinyin input to Chinese character candidates
   * @param pinyinInput - The pinyin string entered by user
   * @returns Array of candidate Chinese characters/words
   */
  public static getCandidates(pinyinInput: string): PinyinCandidate[] {
    if (!pinyinInput.trim()) {
      return [];
    }

    const normalizedInput = pinyinInput.toLowerCase().trim();
    const candidates: PinyinCandidate[] = [];

    // First, try exact matches for multi-character words
    const exactMatch = pinyinToHanziMap[normalizedInput];
    if (exactMatch) {
      exactMatch.forEach((text, index) => {
        candidates.push({
          text,
          pinyin: normalizedInput,
          frequency: 10 - index, // Higher frequency for earlier matches
        });
      });
    }

    // Then, try partial matches for longer words
    Object.keys(pinyinToHanziMap).forEach((key) => {
      if (key.startsWith(normalizedInput) && key !== normalizedInput) {
        pinyinToHanziMap[key].forEach((text, index) => {
          candidates.push({
            text,
            pinyin: key,
            frequency: 5 - index, // Lower frequency for partial matches
          });
        });
      }
    });

    // Sort by frequency (higher first) and limit to top 3
    return candidates.sort((a, b) => b.frequency - a.frequency).slice(0, 3);
  }

  /**
   * Check if the input looks like Pinyin (contains only letters and valid pinyin patterns)
   * @param input - The input string to check
   * @returns boolean indicating if this looks like pinyin input
   */
  public static isPinyinInput(input: string): boolean {
    if (!input.trim()) return false;

    // Basic check: only contains letters (no Chinese characters)
    const letterOnlyPattern = /^[a-zA-Z\s]+$/;
    return letterOnlyPattern.test(input);
  }

  /**
   * Get the Pinyin representation of Chinese text (for validation/debugging)
   * @param chineseText - Chinese characters to convert to pinyin
   * @returns Pinyin representation
   */
  public static chineseToPinyin(chineseText: string): string {
    try {
      return pinyin(chineseText, {
        style: pinyin.STYLE_NORMAL,
        heteronym: false,
        segment: true,
      })
        .flat()
        .join("");
    } catch (error) {
      console.error("Error converting Chinese to Pinyin:", error);
      return "";
    }
  }
}
